import * as Yup from 'yup'
import { isEmpty, toNumber } from 'lodash'

import { customStaticRanges } from '@/components/helpers'
import {
  scoredOptions,
  sentimentOptions,
  attachmentOptions,
  directionOptions,
  CHANNEL_TYPE,
  agentTypeOptions,
  MESSAGE_CHANNEL_TYPES,
  CHANNELS_WITH_CUSTOMER_ID,
} from '@/utils/constants'
import {
  CALL_ONLY_FIELDS,
  COMMON_FIELDS,
  MESSAGE_ONLY_FIELDS,
  QA_ACTIVITY_FILTER_TYPE,
} from '@/reducers/savedFilters/savedFilters.constants'
import { hasChannelAccess } from '@/utils/helpers'

export const buildSchema = (
  data,
  loading,
  filterFormOptions = [],
  existingValues,
  handleDateChangeSideEffect,
  handleChannelChangeSideEffect,
  selectedScorecards = [],
  handleScorecardChange,
  filterType
) => {
  const dispositionsOptions = data.dispositionsOptions || []
  const isOmnichannelEnabled = data?.isOmnichannelEnabled || false
  const currentChannel = data?.currentChannel ?? existingValues?.channel ?? CHANNEL_TYPE.CALL
  const activeChannels = data?.activeChannels || []
  const isBaltoAdmin = data?.isBaltoAdmin || false

  const isFieldVisibleForChannel = (fieldName) => {
    // In step with the filter modal: otherwise a saved SMS filter silently
    // drops the customer id it was created with.
    if (fieldName === 'voipCustomerIds') {
      return CHANNELS_WITH_CUSTOMER_ID.includes(currentChannel)
    }
    if (COMMON_FIELDS.includes(fieldName)) return true

    // If channel is 'call', hide message-specific fields
    if (currentChannel === CHANNEL_TYPE.CALL) {
      return !MESSAGE_ONLY_FIELDS.includes(fieldName)
    }

    // If channel is message (Email, SMS, Chat), hide call-specific fields
    if (MESSAGE_CHANNEL_TYPES.includes(currentChannel)) {
      return !CALL_ONLY_FIELDS.includes(fieldName)
    }

    return true
  }

  const nameSchema = {
    name: 'name',
    type: 'text',
    label: 'Filter Name',
    required: true,
    placeholder: 'Name',
  }

  const defaultSchema = {
    name: 'isDefault',
    type: 'checkbox',
    label: 'Set as default filter',
  }

  const staticSchema = {
    type: 'header',
    label: 'Filter Options',
    name: 'filterPropertiesHeader',
    isStatic: true,
    as: 'h4',
  }

  const channelOptions = [
    { label: 'Call', value: CHANNEL_TYPE.CALL },
    {
      label: 'Email',
      value: CHANNEL_TYPE.EMAIL,
      disabled: !hasChannelAccess(activeChannels, CHANNEL_TYPE.EMAIL, isBaltoAdmin),
    },
    {
      label: 'SMS',
      value: CHANNEL_TYPE.SMS,
      disabled: !hasChannelAccess(activeChannels, CHANNEL_TYPE.SMS, isBaltoAdmin),
    },
    {
      label: 'Chat',
      value: CHANNEL_TYPE.CHAT,
      disabled: !hasChannelAccess(activeChannels, CHANNEL_TYPE.CHAT, isBaltoAdmin),
    },
  ]

  const channelSchema = {
    name: 'channel',
    type: 'radioGroup',
    label: 'Channel',
    options: channelOptions,
    onChange: (val) => handleChannelChangeSideEffect?.(val || CHANNEL_TYPE.CALL),
    isVisible: isOmnichannelEnabled,
  }

  const dateRangeSchema = {
    name: 'dateRange',
    label: 'Date Range',
    placeholder: 'Select Date Range',
    options: customStaticRanges.map((staticRange) => {
      return {
        value: staticRange.label.toLowerCase(),
        label: staticRange.label,
      }
    }),
    type: 'select',
    onChange: (option) => {
      if (handleDateChangeSideEffect) {
        handleDateChangeSideEffect(option?.value || 'today')
      }
    },
  }

  const isManagersLabel =
    filterType === QA_ACTIVITY_FILTER_TYPE || filterFormOptions.includes('playlists')
  const agentSchema = {
    name: 'agents',
    label: isManagersLabel ? 'Managers' : 'Agents',
    type: 'multiSelect',
    placeholder: isManagersLabel ? 'Search Managers' : 'Search Agents',
    options: data.agents,
    condenseOptions: false,
    loading: loading.agents,
    showFullOptions: true,
  }

  const tagSchema = {
    name: 'tags',
    label: 'Tags',
    options: data.tags,
    condenseOptions: false,
    placeholder: 'Select Tags',
    type: 'multiSelect',
    loading: loading.tags,
    showFullOptions: true,
  }

  const organizationsSchema = {
    name: 'organizations',
    label: 'Organizations',
    options: data.organizations,
    condenseOptions: false,
    placeholder: 'Select Organizations',
    type: 'multiSelect',
    loading: loading.organizations,
    showFullOptions: true,
  }

  const playbookSchema = {
    name: 'playbooks',
    label: 'Playbooks',
    options: data.playbooks,
    condenseOptions: false,
    placeholder: 'Select Playbook(s)',
    type: 'multiSelect',
    loading: loading.playbooks,
  }

  const playlistsSchema = {
    name: 'playlists',
    label: 'Playlists',
    options: data.playlists,
    condenseOptions: false,
    placeholder: 'Select Playlist(s)',
    type: 'multiSelect',
    loading: loading.playlists,
    showFullOptions: true,
  }

  const voipCampaignsSchema = {
    name: 'voipCampaigns',
    label: 'VOIP Campaigns',
    options: data.voipCampaigns,
    condenseOptions: false,
    placeholder: 'Select VOIP Campaign(s)',
    type: 'multiSelect',
    loading: loading.voipCampaigns,
  }

  const minDurationSchema = {
    name: 'callDuration',
    label: 'Min. Call Duration',
    type: 'text',
    inputType: 'number',
    customValidators: [
      Yup.number().when('maxCallDuration', {
        is: (val) => toNumber(val) > 0,
        then: Yup.number()
          .label('Min. Call Duration')
          .min(0)
          .lessThan(Yup.ref('maxCallDuration'), 'Min must be less than max'),
        otherwise: Yup.number().label('Min. Call Duration').min(0),
      }),
    ],
    validationDependsOn: 'maxCallDuration',
  }

  const maxDurationSchema = {
    name: 'maxCallDuration',
    label: 'Max. Call Duration',
    type: 'text',
    inputType: 'number',
    customValidators: [
      Yup.number().when('callDuration', {
        is: (val) => toNumber(val) > 0,
        then: Yup.number()
          .label('Max. Call Duration')
          .positive()
          .moreThan(Yup.ref('callDuration'), 'Max must be greater than min'),
        otherwise: Yup.number().label('Max. Call Duration').positive(),
      }),
    ],
    validationDependsOn: 'callDuration',
  }

  const minMessageCountSchema = {
    name: 'minMessageCount',
    label: 'Min Message Count',
    type: 'text',
    inputType: 'number',
    customValidators: [
      Yup.number()
        .transform((v, orig) => (orig === '' || orig == null ? undefined : v))
        .min(0, 'Must be at least 0')
        .when('maxMessageCount', (max, ss) =>
          max !== '' && max != null ? ss.max(+max, 'Min must be less than or equal to Max') : ss
        ),
    ],
    validationDependsOn: 'maxMessageCount',
  }

  const maxMessageCountSchema = {
    name: 'maxMessageCount',
    label: 'Max Message Count',
    type: 'text',
    inputType: 'number',
    customValidators: [
      Yup.number()
        .transform((v, orig) => (orig === '' || orig == null ? undefined : v))
        .min(1, 'Must be at least 1')
        .when('minMessageCount', (min, ss) =>
          min !== '' && min != null ? ss.min(+min, 'Max must be greater than or equal to Min') : ss
        ),
    ],
    validationDependsOn: 'minMessageCount',
  }

  const includeManagersSchema = {
    name: 'includeManagers',
    label: 'Include Managers',
    type: 'checkbox',
  }

  const winsSchema = {
    name: 'isWin',
    label: 'Wins',
    type: 'select',
    options: [
      { value: 'true', label: 'Only wins' },
      { value: 'false', label: 'Only non-wins' },
    ],
  }

  const agentTypeSchema = {
    name: 'agentType',
    label: 'Agent Type',
    type: 'select',
    options: agentTypeOptions,
  }

  const voipCustomerIdSchema = {
    name: 'voipCustomerIds',
    label: 'Customer ID',
    type: 'text',
    placeholder: 'Enter Customer ID',
  }
  const hasSelectedScorecards = !isEmpty(selectedScorecards)

  const scoredStatusSchema = {
    name: 'scoredStatus',
    label: 'Scored Status',
    options: scoredOptions,
    condenseOptions: false,
    type: 'select',
    disabled: !hasSelectedScorecards,
    resetOnDisable: true,
  }
  const scorecardThresholdSchema = {
    name: 'scorecardThreshold',
    label: 'Threshold',
    options: [
      { label: 'Needs improvement', value: 'improvement_threshold' },
      { label: 'Meets expectations', value: 'meets_threshold' },
      { label: 'Exceeds expectations', value: 'exceeds_threshold' },
    ],
    condenseOptions: false,
    type: 'select',
    disabled: !hasSelectedScorecards,
    resetOnDisable: true,
  }
  const scorecardSchema = {
    name: 'scorecards',
    label: 'Scorecards',
    options: data.scorecardConfigs,
    condenseOptions: false,
    placeholder: 'Select Scorecard(s)',
    type: 'multiSelect',
    loading: loading.scorecards,
    customOnChange: handleScorecardChange,
  }

  const keywordStaticSchema = {
    type: 'header',
    label: 'Keyword Options',
    name: 'keywordHeader',
    isStatic: true,
    as: 'h4',
  }

  const keywordsSchema = {
    name: 'keywords',
    label: 'Keywords',
    type: 'text',
  }

  const keywordLogicOption = {
    name: 'keywordOptions.logic',
    label: 'Keyword Logic',
    type: 'radioGroup',
    options: [
      { label: 'And', value: 'and' },
      { label: 'Or', value: 'or' },
    ],
  }

  const keywordSideOption = {
    name: 'keywordOptions.side',
    label: 'Keyword Side',
    type: 'radioGroup',
    options: [
      { label: 'Me', value: 'me' },
      { label: 'Them', value: 'them' },
      { label: 'Both', value: 'both' },
    ],
  }

  const dispositionsStaticSchema = {
    type: 'header',
    label: 'Dispositions Options',
    name: 'dispositionsHeader',
    isStatic: true,
    as: 'h4',
  }
  const dispositionsSchema = {
    name: 'dispositions',
    label: 'Dispositions',
    type: 'nestedSelect',
    options: dispositionsOptions,
  }

  const playbookStaticSchema = {
    type: 'header',
    label: 'Playbook Options',
    name: 'playbookPropertiesHeader',
    isStatic: true,
    as: 'h4',
  }

  // Call Explorer
  const checklistSchema = {
    name: 'checklist',
    label: 'Checklist',
    type: 'nestedSelectReadOnly',
  }

  const deckSchema = {
    name: 'deck',
    label: 'Dynamic Prompt',
    type: 'nestedSelectReadOnly',
  }

  const notificationsSchema = {
    name: 'notifications',
    label: 'Notifications',
    type: 'nestedSelectReadOnly',
  }

  const postcallSchema = {
    name: 'postcall',
    label: 'Post Call',
    type: 'nestedSelectReadOnly',
  }

  const endingSentimentSchema = {
    name: 'endingSentiment',
    label: 'Customer Ending Sentiment',
    type: 'multiSelect',
    options: sentimentOptions,
  }

  const attachmentsSchema = {
    name: 'attachments',
    label: 'Attachments',
    type: 'multiSelect',
    options: attachmentOptions,
  }

  const directionSchema = {
    name: 'direction',
    label: 'Call Direction',
    type: 'select',
    options: directionOptions,
  }

  const allOptions = [
    nameSchema,
    defaultSchema,
    staticSchema,
    channelSchema,
    dateRangeSchema,
    organizationsSchema,
    agentSchema,
    tagSchema,
    playbookSchema,
    playlistsSchema,
    voipCampaignsSchema,
    minDurationSchema,
    maxDurationSchema,
    winsSchema,
    includeManagersSchema,
    keywordStaticSchema,
    keywordsSchema,
    keywordLogicOption,
    keywordSideOption,
    dispositionsStaticSchema,
    dispositionsSchema,
    playbookStaticSchema,
    checklistSchema,
    deckSchema,
    notificationsSchema,
    postcallSchema,
    voipCustomerIdSchema,
    scorecardSchema,
    scoredStatusSchema,
    scorecardThresholdSchema,
    endingSentimentSchema,
    attachmentsSchema,
    directionSchema,
    minMessageCountSchema,
    maxMessageCountSchema,
    agentTypeSchema,
  ]

  return allOptions
    .filter(
      (opt) =>
        opt.isVisible !== false &&
        filterFormOptions.includes(opt.name) &&
        isFieldVisibleForChannel(opt.name)
    )
    .map(({ isVisible, ...rest }) => rest)
}
